import {Alert, Box, Button, IconButton, InputAdornment, Paper, Stack, styled, Typography} from "@mui/material";
import MuiOutlinedInput from "@mui/material/OutlinedInput";
import HeaderBreadcrumb from "../components/HeaderBreadcrumb";
import {storeLocalData, useAuth} from "../auth/AuthProvider";
import {useState} from "react";
import axios from "axios";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Section = styled(Paper)(({theme}) => ({
    backgroundColor: theme.palette.admin.light,
    color: theme.palette.admin.contrastText,
}));

const Title = styled(Typography)(({theme}) => ({
    padding: '0.5rem',
    fontSize: theme.typography.h6.fontSize,
}));

const SubmitButton = styled(Button)(({theme}) => ({
    backgroundColor: theme.palette.contrastGrey.main,
    color: '#000',
    textTransform: 'none',
    "&:hover": {
        backgroundColor: theme.palette.contrastGrey.dark,
        color: '#fff',
    },
    [theme.breakpoints.up('xs')]: {
        width: '100%',
    },
    [theme.breakpoints.up('md')]: {
        width: '25%',
    },
}));

const OutlinedInput = styled(MuiOutlinedInput)(({theme}) => ({
    [theme.breakpoints.up('xs')]: {
        width: '100%',
    },
    [theme.breakpoints.up('md')]: {
        width: '25%',
    },
}));

const PasswordField = ({id, ...rest}) => {
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();
    const handleMouseUpPassword = (event) => event.preventDefault();

    return (
        <OutlinedInput
            id={id}
            name={id}
            type={showPassword ? 'text' : 'password'}
            size="small"
            required
            {...rest}
            endAdornment={
                <InputAdornment position="end">
                    <IconButton
                        aria-label={
                            showPassword ? 'hide the password' : 'display the password'
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                    >
                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                    </IconButton>
                </InputAdornment>
            }
        />
    );
};

const MyDetails = () => {
    const {user, token, setToken} = useAuth();
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();

        const changePasswordData = new FormData(event.currentTarget);
        axios.post("/auth/changePassword", changePasswordData, {auth: token})
            .then((response) => {
                const auth = {
                    username: token.username,
                    password: changePasswordData.get("new_password"),
                };

                storeLocalData("token", auth, false);
                setToken(auth);

                setSuccessMessage(response.data);
                setErrorMessage("");
            })
            .catch((error) => {
                setSuccessMessage("");
                setErrorMessage(error.response.data);
            });
    };

    return (
        <Stack>
            <HeaderBreadcrumb name="My Details" color="admin"/>
            <Stack sx={{padding: '1rem'}} spacing={2}>
                <Paper variant="outlined" sx={{border: 'none'}}>
                    <Stack spacing={2}>
                        <Section elevation={0}>
                            <Title>My Details</Title>
                        </Section>
                        <Stack sx={{padding: '0 0 1rem 1rem'}} spacing={2}>
                            <Typography id="name" name="name">Name: {user.firstName} {user.lastName}</Typography>
                            <Typography id="email" name="email">Username: {user.email}</Typography>
                        </Stack>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{border: 'none'}}>
                    <Stack spacing={2}>
                        <Section elevation={0}>
                            <Title>Change Password</Title>
                        </Section>

                        {successMessage.length > 0 && (<Alert severity="success">{successMessage}</Alert>)}
                        {errorMessage.length > 0 && (<Alert severity="error">{errorMessage}</Alert>)}

                        <Box component="form" onSubmit={handleSubmit}>
                            <Stack sx={{padding: '0 1rem 1rem 1rem'}} spacing={2}>
                                <Stack>
                                    <Typography>Old password</Typography>
                                    <PasswordField id="old_password" autoFocus/>
                                </Stack>

                                <Stack>
                                    <Typography>New password (at least 6 characters)</Typography>
                                    <PasswordField id="new_password" inputProps={{minLength: 6}}/>
                                </Stack>

                                <Stack>
                                    <Typography>Retype new password</Typography>
                                    <PasswordField id="confirm_password"/>
                                </Stack>

                                <SubmitButton type="submit" variant="contained">Submit</SubmitButton>
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>
        </Stack>
    );
};

export default MyDetails;
